import React, { createContext, useState, useEffect, useMemo, useContext, useCallback } from 'react';
import { useGeneral } from '../../Context/GeneralContext';

const CuentaContext = createContext();

export function CuentaProvider(props) {
    const { tokenR } = useGeneral();
    const [userName, setUserName] = useState('');
    const [roles, setRoles] = useState([]);
    const [passError, setPassError] = useState(true);
    const [nueva, setNueva] = useState('');
    const [equal, setEqual] = useState(false);
    const [snackMsg, setSnackMsg] = useState('');
    const [openSnack, setOpenSnack] = useState(false);
    const [theUser, setTheUser] = useState({
        usuario: '',
        password: '',
        email: '',
        phoneNumber: '',
        id: null,
        nombre: '',
        apellido: '',
        cardId: null,
        notas: '',
        departamento: 0,
        grupo: 0,
        esNivel1: false,
        esNivel2: false,
        esNivel3: false,
        idioma: 0,
        horario: null,
        roles: [],
    });

    const ordenaArrObj = (x, y) => {
        if (x.nombre < y.nombre) return -1;
        if (x.nombre > y.nombre) return 1;
        return 0;
    };

    const getInfo = useCallback(
        async (dto) => {
            try {
                const res = await fetch(`${process.env.REACT_APP_SERVICE_ENDPOINT}/${dto}`, {
                    headers: {
                        authorization: `Bearer ${tokenR.current}`,
                        Accept: 'application/json',
                    },
                });
                if (!res.ok) return;
                const obj = await res.json();
                if (dto === 'roles') {
                    const cargos = obj.roles.map((cargo) => ({
                        nombre: cargo.toString(),
                        color: 'default',
                    }));
                    cargos.sort(ordenaArrObj);
                    setRoles(cargos);
                }
            } catch (err) {
                console.error(err);
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        },
        [tokenR]
    );

    const getTheUser = useCallback(
        async (user) => {
            try {
                const res = await fetch(
                    `${process.env.REACT_APP_SERVICE_ENDPOINT}/usuarios/GetUsuario?username=${user}`,
                    {
                        headers: {
                            authorization: `Bearer ${tokenR.current}`,
                            Accept: 'application/json',
                        },
                    }
                );
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const data = await res.json();
                if (data && Array.isArray(data.roles)) {
                    setTheUser(data);
                } else {
                    setTheUser({ roles: [] });
                }
            } catch (err) {
                console.error(err);
                setTheUser({ roles: [] });
            }
        },
        [tokenR]
    );

    const getUserName = useCallback(async () => {
        try {
            const res = await fetch(
                `${process.env.REACT_APP_SERVICE_ENDPOINT}/usuarios/ReturnUsuario`,
                {
                    headers: {
                        authorization: `Bearer ${tokenR.current}`,
                        Accept: 'application/json',
                    },
                }
            );
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data = await res.json();
            if (typeof data === 'string') setUserName(data);
        } catch (err) {
            console.error(err);
        }
    }, [tokenR]);

    const putUser = useCallback(
        async (data) => {
            try {
                const res = await fetch(
                    `${process.env.REACT_APP_SERVICE_ENDPOINT}/usuarios/Actualizar`,
                    {
                        method: 'PUT',
                        body: JSON.stringify(data),
                        headers: {
                            authorization: `Bearer ${tokenR.current}`,
                            Accept: 'application/json',
                            'Content-Type': 'application/json',
                        },
                    }
                );
                const response = await res.json();
                setSnackMsg(response);
                setOpenSnack(true);
                getTheUser(userName);
            } catch (err) {
                console.error(err);
            }
        },
        [tokenR, getTheUser, userName]
    );

    const changePass = useCallback(
        async (data) => {
            try {
                const res = await fetch(
                    `${process.env.REACT_APP_SERVICE_ENDPOINT}/usuarios/cambiarcontrasena`,
                    {
                        method: 'PUT',
                        body: JSON.stringify(data),
                        headers: {
                            authorization: `Bearer ${tokenR.current}`,
                            Accept: 'application/json',
                            'Content-Type': 'application/json',
                        },
                    }
                );
                const response = await res.json();
                setSnackMsg(response);
                setOpenSnack(true);
                getTheUser(userName);
            } catch (err) {
                console.error(err);
            }
        },
        [tokenR, getTheUser, userName]
    );

    const buscaMinus = (p) => [...p].some((c) => c >= 'a' && c <= 'z');
    const buscaMayus = (p) => [...p].some((c) => c >= 'A' && c <= 'Z');
    const buscaNumero = (p) => /\d/.test(p);
    const buscaEspecial = (p) => /[^a-zA-Z0-9]/.test(p);

    const validaPass = useCallback((pass) => {
        setNueva(pass);
        setPassError(
            !(
                pass.length > 5 &&
                buscaMinus(pass) &&
                buscaMayus(pass) &&
                buscaEspecial(pass) &&
                buscaNumero(pass)
            )
        );
    }, []);

    const comparePass = useCallback(
        (pass) => {
            setEqual(nueva === pass);
        },
        [nueva]
    );

    useEffect(() => {
        getUserName();
        getInfo('roles');
    }, [getInfo, getUserName]);

    useEffect(() => {
        if (userName !== '') getTheUser(userName);
    }, [getTheUser, userName]);

    const CuentaValues = useMemo(
        () => ({
            theUser,
            getTheUser,
            roles,
            setRoles,
            passError,
            setPassError,
            nueva,
            equal,
            userName,
            snackMsg,
            setSnackMsg,
            openSnack,
            setOpenSnack,
            ordenaArrObj,
            validaPass,
            putUser,
            changePass,
            comparePass,
        }),
        [
            theUser,
            getTheUser,
            roles,
            passError,
            nueva,
            equal,
            userName,
            snackMsg,
            openSnack,
            validaPass,
            putUser,
            changePass,
            comparePass,
        ]
    );

    return <CuentaContext.Provider value={CuentaValues} {...props} />;
}

export function useCuenta() {
    const context = useContext(CuentaContext);
    if (!context) throw new Error('useCuenta debe estar dentro del proveedor CuentaContext');
    return context;
}
